import { GuideQuery } from '__generated__v2/graphql';
import { CtxMapV2 } from 'components/mapV2/contexts/CtxMapV2';
import { MapV2 } from 'components/mapV2/MapV2';
import { useZoomToMarker } from 'hooks/map/useZoomToMarker';
import { useCtxMapView } from 'pages/special/OsloDesignFair/map/CtxMapView';
import { divideMarkers } from 'pages/special/OsloDesignFair/map/divideMarkers';
import { GlobalLayerIds } from 'pages/special/OsloDesignFair/map/mapContent/Markers/constants';
import { WorldMarkers } from 'pages/special/OsloDesignFair/map/markers/WorldMarkers';
import { useContext, useEffect } from 'react';
import { useParams } from 'react-router';
import { CtxModal } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';

interface Props {
    allData: GuideQuery;
}

export const OsloDesignFairMap = (props: Props) => {
    const { allData } = props;
    const ctxView = useContext(CtxView);
    const ctxMapView = useCtxMapView();
    const ctxModal = useContext(CtxModal);
    const params = useParams() as { detailId?: string };
    const zoomToMarker = useZoomToMarker();
    const ctxMapV2 = useContext(CtxMapV2);

    useEffect(() => {
        if (params.detailId) {
            const detail = allData.app_guide.details.find((e) => e.id === params.detailId);
            if (detail) {
                ctxModal.setDetailModal({
                    id: detail.id,
                    detail: ctxView.allDetails.find((e) => e.storyId === detail.id),
                    markerId: detail.connectedMarkerIds[0]
                });
                zoomToMarker.zoomToMarker(detail.connectedMarkerIds[0]);
            }
        }
    }, []);

    useEffect(() => {
        if (ctxModal.detailModal?.id) {
            const highlighId = allData.app_guide.details.find(
                (e) => e.id === ctxModal.detailModal?.id
            )?.connectedMarkerIds[0];
            ctxMapView.setHighlightedMarkerIds([highlighId]);
            zoomToMarker.zoomToMarker(ctxModal.detailModal.markerId);
        } else {
            ctxMapView.setHighlightedMarkerIds([]);
        }
    }, [ctxModal.detailModal?.id]);

    return (
        <MapV2
            onMapClickedOverride={() => {
                ctxMapView.setForceHighlightSelectableMarkers();
                return true;
            }}
            onMarkerSelected={(id) => {
                const detail = allData.app_guide.details.find((e) =>
                    e.connectedMarkerIds.includes(id)
                );
                if (detail) {
                    ctxModal.setDetailModal({
                        id: detail.id,
                        detail: ctxView.allDetails.find((e) => e.storyId === detail.id),
                        markerId: id
                    });
                    ctxMapV2.mobileModalState.setSize('large');
                    return true;
                }
                return false;
            }}
            dragRotate={true}
            touchPitch={true}
            interactiveLayerIds={[
                GlobalLayerIds.IndoorStandCircles,
                GlobalLayerIds.IndoorStandSymbols,
                GlobalLayerIds.IndoorStandWalls_Inner
            ]}
            mapChildren={
                <>
                    <WorldMarkers
                        dividedMarkers={divideMarkers(
                            allData.app_guide?.map?.markers.map((marker) => {
                                if (marker.__typename !== 'MapMarkerIndoorStand') {
                                    return marker;
                                }
                                if (ctxView.visibleMapSetIds.length > 1) {
                                    return { ...marker, selectable: true };
                                }

                                const attributeId =
                                    allData.app_guide?.map?.filters?.[0]?.attributeId;
                                const categoryId = ctxView.visibleMapSetIds?.[0] ?? null;
                                const connectedDetail = allData.app_guide.details.find((e) =>
                                    e.connectedMarkerIds.includes(marker.id)
                                );
                                if (!connectedDetail || !categoryId) {
                                    return { ...marker, selectable: true };
                                }

                                const attr = connectedDetail.attributes.find(
                                    (e) => e.attributeId === attributeId
                                );

                                if (!attr) {
                                    return {
                                        ...marker,
                                        selectable: false
                                    };
                                }

                                if (attr.__typename === 'DetailAttributeValueOption') {
                                    if (attr.valueOption === categoryId) {
                                        return {
                                            ...marker,
                                            selectable: true
                                        };
                                    } else {
                                        return {
                                            ...marker,
                                            selectable: false
                                        };
                                    }
                                }
                                if (attr.__typename === 'DetailAttributeValueOptions') {
                                    if (attr.valueOptions.includes(categoryId)) {
                                        return marker;
                                    } else {
                                        return {
                                            ...marker,
                                            selectable: false
                                        };
                                    }
                                }
                                return marker;
                            }) ?? [],
                            { isEditMode: false, selectedIds: [] }
                        )}
                    />
                </>
            }
        />
    );
};
